import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Markdown from "@/components/legal/Markdown";
import { getLegalDocument } from "@/lib/api";

/**
 * Renders a legal document from its Markdown source over the API.
 *
 * Nothing is copied into the bundle. The file in /legal is the only copy, which
 * is what stops the published page and the accepted text from drifting apart.
 */
export default function LegalPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, doc: null, error: null });

  useEffect(() => {
    let live = true;
    setState({ loading: true, doc: null, error: null });
    (async () => {
      try {
        const doc = await getLegalDocument(slug);
        if (live) setState({ loading: false, doc, error: null });
      } catch (e) {
        if (live) setState({
          loading: false, doc: null,
          error: e?.response?.status === 404 ? "notfound" : "failed",
        });
      }
    })();
    return () => { live = false; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#e8e4df]">
      <Nav />
      <main className="max-w-[820px] mx-auto px-6 md:px-10 pt-28 pb-20">
        <Link to="/" className="tick inline-flex items-center gap-1.5 mb-8 hover:text-[#171717]">
          <ArrowLeft size={12} /> Back to home
        </Link>

        {state.loading && (
          <div className="flex items-center gap-2 font-mono-tech text-[13px] text-[#525252]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {state.error === "notfound" && (
          <div>
            <h1 className="font-serif-display text-4xl text-[#171717] mb-3">Not found.</h1>
            <p className="font-mono-tech text-[13px] text-[#525252]">
              No legal document with that address.
            </p>
          </div>
        )}

        {state.error === "failed" && (
          <div>
            <h1 className="font-serif-display text-4xl text-[#171717] mb-3">Could not load.</h1>
            <p className="font-mono-tech text-[13px] text-[#525252]">
              Please reload the page.
            </p>
          </div>
        )}

        {state.doc && (
          <article data-testid={`legal-${slug}`}>
            {state.doc.is_draft && (
              <div className="mb-8 flex gap-2.5 items-start px-4 py-3 border border-[#9a3412]/40 bg-[#9a3412]/[0.06] rounded">
                <AlertTriangle size={14} className="text-[#9a3412] mt-0.5 shrink-0" />
                <p className="font-mono-tech text-[12px] text-[#9a3412] leading-relaxed">
                  <strong className="font-semibold">Draft, not in force.</strong>{" "}
                  This document has not been reviewed by an Australian legal
                  practitioner. It is published here for review only.
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-8 pb-4 border-b border-[#c9c3b8]">
              <span className="tick text-[#9a3412]">Version {state.doc.version}</span>
              <span className="tick">
                {String(state.doc.effective_date).startsWith("TODO")
                  ? "Not yet effective"
                  : `Effective ${state.doc.effective_date}`}
              </span>
            </div>

            <Markdown source={state.doc.body} />

            <p className="mt-12 pt-5 border-t border-[#ddd8ce] font-mono-tech text-[11px] text-[#8a857d] break-all">
              Document {state.doc.document_id} · integrity hash {state.doc.content_hash}
            </p>
          </article>
        )}
      </main>
      <Footer />
    </div>
  );
}
